import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ApiProvider } from './api.provider';
import { IonLoading, ToastController } from '@ionic/angular';
import { AuthService } from './auth/auth.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CoreProvider {
  public readonly backDeviceSearchRegex = /back|trás|rear|traseira|environment|ambiente/gi;

  constructor(
    public readonly api: ApiProvider,
    public readonly auth: AuthService,
    public readonly toastCtrl: ToastController,
    public readonly alertCtrl: ToastController,
    public readonly router: Router,
  ) {
    if (!environment.production && window)
      (window as unknown as { core: CoreProvider })['core'] = this;
    auth.initCore(this);
  }

  public redirectToUserAllowedDefaultPage(dryRun = false) {
    let url = '';
    switch (this.auth.user?.role) {
      case 'ADMIN':
        url = '/admin/dashboard';
        break;
      case 'VALIDATOR':
        url = '/validator/dashboard';
        break;
      case 'COLLABORATOR':
        url = '/collaborator/dashboard';
        break;
      case 'USER':
        url = '/user/dashboard';
        break;
      default:
        console.error('No role found, probably not logged in');
        url = '/auth/login';
        break;
    }

    if (!dryRun && url) this.router.navigate([url]);
    return url;
  }

  public async successToast(loading?: IonLoading | LoadingStatusHandler, message?: string) {
    if (loading) {
      if (loading instanceof IonLoading) loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setSuccess();
    }

    await (
      await this.toastCtrl.create({
        message: message || 'Se ha completado la acción correctamente',
        duration: 3000,
        color: 'success',
      })
    ).present();
  }

  public async errorToast(
    loading?: IonLoading | LoadingStatusHandler,
    message: any | string = null,
    duration = null
  ) {
    if (loading) {
      if (loading instanceof IonLoading) loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setError();
    }

    if (typeof message != 'string' && message != null) {
      if (
        message.status &&
        message.status == 401 &&
        !this.auth.isAuthenticated
      ) {
        this.auth.logout();
      }
      message = message instanceof Error ? message.message : this.fetchErrMsg(message.error);
    }

    return await (
      await this.toastCtrl.create({
        message:
          message || 'Se ha producido un error, inténtelo de nuevo más tarde',
        duration: duration || 10000,
        color: 'danger',
        buttons: [{ text: 'OK', role: 'cancel' }],
      })
    ).present();
  }
  public fetchErrMsg(err: any) {
    if (typeof err == 'string') {
      try {
        err = JSON.parse(err);
      } catch (e) {
        return err;
      }
    }

    if (err.message?.[0]?.constraints) return err.message.reduce((acc: string, itm: any) =>
      acc + '\n' + Object.keys(itm.constraints).reduce((acc2: string, key: string) =>
        acc2 + '\n - ' + itm.constraints[key]
      , '')
    , '');
    if (err.error && err.message) return `${err.error}: ${err.message}`;
    if (err.error) return err.error;
    if (err.errors) {
      let msg = 'Se han producido los siguientes errores: ';
      for (let itm in err.errors) {
        msg += '\n- ' + err.errors[itm].toString();
      }
      return msg;
    }
    if (Object.getPrototypeOf(err).toString().indexOf('ProgressEvent'))
      return 'Se ha producido un error de comunicación, por favor, compruebe su conexión.';
    return err.toString();
  }
}

export type LoadingStatusType = 'error' | 'loading' | 'success' | 'idle';
export class LoadingStatusHandler extends BehaviorSubject<LoadingStatusType> {
  timeout: any;
  constructor(initial: LoadingStatusType = 'idle') {
    super(initial);
  }
  override next(value: LoadingStatusType): void {
    throw new Error('Setting status directly is not allowed');
  }
  setIdle() {
    super.next('idle');
    this.clearTimeout();
  }
  setLoading() {
    super.next('loading');
    this.clearTimeout();
  }
  setError() {
    super.next('error');
    this.clearTimeout();
  }
  setSuccess() {
    super.next('success');
    this.clearTimeout();
    this.timeout = setTimeout(() => {
      this.setIdle();
    }, 3000);
  }

  private clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}
